import {
  useQuery,
  useQueryClient,
  UseQueryResult,
} from "@tanstack/react-query";
import api from "@/config/axiosConfig";

interface Location {
  name: string;
  id: string;
}

interface DistanceInfo {
  distance: string;
  duration: string;
}

interface TransportModes {
  driving: DistanceInfo;
  walking: DistanceInfo;
  transit: DistanceInfo;
}

interface RestructuredDistanceItem extends TransportModes {
  origin: Location;
  destination: Location;
}

interface RestructuredDistanceData {
  [key: string]: RestructuredDistanceItem;
}

async function getDistanceMatrix(
  items: any[]
): Promise<RestructuredDistanceData> {
  const response = await api.experiences.post("/place-distances", {
    dayItems: items,
  });
  return response.data;
}

export const useDistanceMatrix = (
  dayItems: any[]
): UseQueryResult<RestructuredDistanceData, Error> => {
  const queryClient = useQueryClient();

  return useQuery<RestructuredDistanceData, Error>({
    queryKey: ["distances", dayItems.map((item) => item.id).join("-")],
    queryFn: async () => {
      if (dayItems.length < 2) {
        console.warn("Not enough items to calculate distances:", dayItems);
        return {};
      }

      // Collect pairs of origins and destinations that need fetching
      const pairsToFetch: {
        origin: any;
        destination: any;
        cacheKey: string;
      }[] = [];
      const result: RestructuredDistanceData = {};

      for (let i = 0; i < dayItems.length - 1; i++) {
        const origin = dayItems[i];
        const destination = dayItems[i + 1];
        const cacheKey = `${origin.id}-${destination.id}`;

        const cachedData = queryClient.getQueryData<RestructuredDistanceItem>([
          "distance",
          cacheKey,
        ]);

        if (
          cachedData &&
          cachedData.driving &&
          cachedData.walking &&
          cachedData.transit
        ) {
          result[cacheKey] = cachedData;
        } else {
          pairsToFetch.push({ origin, destination, cacheKey });
        }
      }

      if (pairsToFetch.length > 0) {
        const locationsMap = new Map<string, any>();
        pairsToFetch.forEach(({ origin, destination }) => {
          locationsMap.set(origin.id, origin);
          locationsMap.set(destination.id, destination);
        });
        const uniqueLocations = Array.from(locationsMap.values());
        // only keep name, id and coordinates
        const finalLocations = uniqueLocations.map((item) => ({
          name: item.name,
          id: item.id,
          coordinates: item.coordinates,
        }));

        if (finalLocations.length < 2) {
          console.warn(
            "Not enough unique locations to calculate distances:",
            finalLocations
          );
          return result;
        }

        const response = await getDistanceMatrix(finalLocations);

        Object.entries(response).forEach(([key, value]) => {
          queryClient.setQueryData(["distance", key], value);
          result[key] = value;
        });
      }

      return result;
    },
    retry: false,
    staleTime: Infinity,
    enabled: dayItems.length > 1,
    refetchOnWindowFocus: false,
  });
};
